import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useOrders, type Order } from '@/hooks/useOrders';
import { useAuthContext } from '@/contexts/AuthContext';
import InvoicePDF from './components/InvoicePDF';

const POLL_INTERVAL_MS = 30000;

const statusConfig: Record<string, { label: string; color: string; icon: string; desc: string }> = {
  paid:             { label: 'Paid',             color: 'bg-green-50 text-green-600 border-green-100',  icon: 'ri-checkbox-circle-fill', desc: 'Payment confirmed' },
  finished:         { label: 'Completed',        color: 'bg-green-50 text-green-600 border-green-100',  icon: 'ri-checkbox-circle-fill', desc: 'Payment confirmed' },
  confirmed:        { label: 'Confirmed',        color: 'bg-green-50 text-green-600 border-green-100',  icon: 'ri-checkbox-circle-fill', desc: 'Payment confirmed' },
  pending:          { label: 'Pending',          color: 'bg-amber-50 text-amber-600 border-amber-100',  icon: 'ri-time-line',            desc: 'Awaiting processing' },
  awaiting_payment: { label: 'Awaiting Payment', color: 'bg-amber-50 text-amber-600 border-amber-100',  icon: 'ri-time-line',            desc: 'Waiting for crypto payment' },
  confirming:       { label: 'Confirming',       color: 'bg-amber-50 text-amber-600 border-amber-100',  icon: 'ri-loader-4-line',        desc: 'Blockchain confirming' },
  failed:           { label: 'Failed',           color: 'bg-red-50 text-red-500 border-red-100',         icon: 'ri-close-circle-line',    desc: 'Payment failed' },
  refunded:         { label: 'Refunded',         color: 'bg-gray-100 text-gray-500 border-gray-200',     icon: 'ri-refund-2-line',        desc: 'Amount refunded' },
  cancelled:        { label: 'Cancelled',        color: 'bg-gray-100 text-gray-400 border-gray-200',     icon: 'ri-close-line',           desc: 'Order cancelled' },
};

const pricingLabel: Record<string, string> = {
  '1_2_year':       '1-2 Year Plan',
  '3_5_year':       '3-5 Year Plan',
  lifetime:         'Lifetime Access',
  price_1_2_year:   '1-2 Year Plan',
  price_3_5_year:   '3-5 Year Plan',
  price_lifetime:   'Lifetime Access',
};

function OrderCard({ order, onClick }: { order: Order; onClick: () => void }) {
  const cfg = statusConfig[order.status] ?? statusConfig.pending;
  const isConfirming = order.status === 'awaiting_payment' || order.status === 'confirming' || order.status === 'pending';
  const isSuccess    = order.status === 'paid' || order.status === 'finished' || order.status === 'confirmed';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-surface-3 hover:border-gray-300 transition-all cursor-pointer group"
      onClick={onClick}
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isSuccess ? 'bg-green-50' : isConfirming ? 'bg-amber-50' : 'bg-red-50'}`}>
              <i className={`${cfg.icon} text-lg ${isSuccess ? 'text-green-500' : isConfirming ? 'text-amber-500 animate-pulse' : 'text-red-400'}`} />
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">{order.product_name}</div>
              <div className="text-xs text-gray-400 mt-0.5">{pricingLabel[order.pricing_type] ?? order.pricing_type}</div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-lg font-bold text-gray-900">${Number(order.amount).toFixed(2)}</div>
            <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${cfg.color}`}>
              <i className={`${cfg.icon} text-xs`} />
              {cfg.label}
            </span>
          </div>
        </div>

        {isConfirming && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1.5">
              <span>Payment Status</span>
              <span className="text-amber-500 font-medium">{cfg.desc}</span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-amber-400 rounded-full animate-pulse" style={{ width: order.status === 'confirming' ? '60%' : '30%' }} />
            </div>
          </div>
        )}

        {isSuccess && (
          <div className="mb-4 bg-green-50 border border-green-100 rounded-lg px-3 py-2 flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-green-500 text-sm" />
            <span className="text-xs text-green-700 font-medium">Subscription activated — check your subscriptions</span>
          </div>
        )}

        <div className="flex items-center justify-between text-[11px] text-gray-400">
          <span className="font-mono">{order.id}</span>
          <span>{new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
        </div>
      </div>
    </motion.div>
  );
}

function OrderDetailModal({
  order, onClose, onDownloadInvoice,
}: {
  order: Order;
  onClose: () => void;
  onDownloadInvoice: () => void;
}) {
  const cfg        = statusConfig[order.status] ?? statusConfig.pending;
  const isSuccess  = order.status === 'paid' || order.status === 'finished' || order.status === 'confirmed';
  const isConfirming = order.status === 'awaiting_payment' || order.status === 'confirming' || order.status === 'pending';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
      >
        {/* Header */}
        <div className={`px-6 py-5 ${isSuccess ? 'bg-green-50 border-b border-green-100' : isConfirming ? 'bg-amber-50 border-b border-amber-100' : 'bg-red-50 border-b border-red-100'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSuccess ? 'bg-green-100' : isConfirming ? 'bg-amber-100' : 'bg-red-100'}`}>
                <i className={`${cfg.icon} text-xl ${isSuccess ? 'text-green-600' : isConfirming ? 'text-amber-600' : 'text-red-500'}`} />
              </div>
              <div>
                <div className="font-bold text-gray-900">{cfg.label}</div>
                <div className="text-xs text-gray-500">{cfg.desc}</div>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/60 cursor-pointer transition-colors">
              <i className="ri-close-line text-gray-500" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <h3 className="font-bold text-gray-900 mb-4">{order.product_name}</h3>

          {isConfirming && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-5">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-amber-500 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-amber-800 mb-1">Payment Processing</div>
                  <div className="text-xs text-amber-700 leading-relaxed">
                    Your crypto payment is being processed. This usually takes 1–30 minutes depending on network congestion. Your subscription activates automatically once confirmed.
                  </div>
                </div>
              </div>
            </div>
          )}

          {isSuccess && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 mb-5">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-green-500 text-lg mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-green-800 mb-1">Subscription Active</div>
                  <div className="text-xs text-green-700">Your subscription has been activated. You can access it from the Subscriptions page.</div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-0 divide-y divide-surface-3">
            {([
              ['Order ID',   order.id],
              ['Product',    order.product_name],
              ['Plan',       pricingLabel[order.pricing_type] ?? order.pricing_type],
              ['Amount',     `$${Number(order.amount).toFixed(2)}`],
              ['Status',     cfg.label],
              ['Payment ID', order.payment_id || '—'],
              ['Date',       new Date(order.created_at).toLocaleString()],
            ] as [string, string][]).map(([k, v]) => (
              <div key={k} className="flex justify-between py-2.5">
                <span className="text-gray-400 text-xs">{k}</span>
                <span className={`text-xs font-medium text-right max-w-[220px] truncate ${k === 'Amount' ? 'text-brand-500 font-bold' : 'text-gray-900'}`}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-2 flex-wrap">
          {isSuccess && (
            <>
              <Link
                to="/subscriptions"
                className="flex-1 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-sm font-semibold text-center cursor-pointer whitespace-nowrap transition-colors"
              >
                View Subscription
              </Link>
              <button
                onClick={onDownloadInvoice}
                className="flex items-center gap-1.5 py-2.5 px-4 bg-surface-2 hover:bg-surface-3 text-gray-700 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-colors border border-surface-3"
              >
                <i className="ri-file-download-line" />
                Invoice PDF
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className={`${isSuccess ? '' : 'flex-1'} py-2.5 px-5 bg-surface-2 hover:bg-surface-3 text-gray-700 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-colors`}
          >
            Close
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function OrdersPage() {
  const { orders, loading, error, refetch } = useOrders();
  const { user } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected]         = useState<Order | null>(null);
  const [invoiceOrder, setInvoiceOrder] = useState<Order | null>(null);
  const [lastPolled, setLastPolled]     = useState<Date | null>(null);
  const [countdown, setCountdown]       = useState(30);
  const pollRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Handle NP_id / success redirect from NOWPayments ─────────────────────
  // NP_id is the NOWPayments payment_id — find the matching order by payment_id
  useEffect(() => {
    const npId   = searchParams.get('NP_id');
    const isSuccess = searchParams.get('success');
    if (!npId && !isSuccess) return;

    if (orders.length === 0) return; // wait until orders are loaded

    if (npId) {
      // Find order whose payment_id matches NP_id
      const match = orders.find(o => o.payment_id === npId);
      if (match) {
        setSelected(match);
      }
    }

    // Clean up the URL params so they don't persist on reload
    setSearchParams({}, { replace: true });
  }, [orders, searchParams, setSearchParams]);

  const hasPending = orders.some(o =>
    o.status === 'awaiting_payment' || o.status === 'confirming' || o.status === 'pending'
  );

  useEffect(() => {
    if (!hasPending) {
      if (pollRef.current)      clearInterval(pollRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      return;
    }
    setCountdown(30);
    pollRef.current = setInterval(async () => {
      await refetch();
      setLastPolled(new Date());
      setCountdown(30);
    }, POLL_INTERVAL_MS);
    countdownRef.current = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? 30 : prev - 1));
    }, 1000);
    return () => {
      if (pollRef.current)      clearInterval(pollRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [hasPending, refetch]);

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    const matchSearch = !q || o.id.toLowerCase().includes(q) || o.product_name.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || o.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const pendingCount   = orders.filter(o => ['awaiting_payment', 'confirming', 'pending'].includes(o.status)).length;
  const completedCount = orders.filter(o => ['paid', 'finished', 'confirmed'].includes(o.status)).length;
  const totalSpent     = orders.filter(o => ['paid', 'finished', 'confirmed'].includes(o.status)).reduce((s, o) => s + Number(o.amount), 0);

  const userName  = user?.email?.split('@')[0] ?? 'Customer';
  const userEmail = user?.email ?? '';

  return (
    <AppLayout>
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">My Orders</h1>
          <p className="text-sm text-gray-400 mt-0.5">Track your payment status and order history</p>
        </div>

        {/* Success banner — shown when redirected back from NOWPayments */}
        {searchParams.get('success') === 'true' && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3"
          >
            <i className="ri-checkbox-circle-fill text-green-500 text-lg shrink-0" />
            <div>
              <span className="text-sm font-semibold text-green-800">Payment submitted successfully!</span>
              <p className="text-xs text-green-600 mt-0.5">Your order is being processed. Subscription activates automatically once blockchain confirms.</p>
            </div>
          </motion.div>
        )}

        {/* Stats */}
        {!loading && orders.length > 0 && (
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total Orders', value: orders.length,            icon: 'ri-shopping-cart-line',        color: 'text-brand-500 bg-brand-50' },
              { label: 'Completed',    value: completedCount,           icon: 'ri-checkbox-circle-line',       color: 'text-green-600 bg-green-50' },
              { label: 'Total Spent',  value: `$${totalSpent.toFixed(2)}`, icon: 'ri-money-dollar-circle-line', color: 'text-slate-600 bg-slate-100' },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                className="bg-white rounded-xl p-4 flex items-center gap-3"
              >
                <span className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 ${s.color}`}>
                  <i className={`${s.icon} text-base`} />
                </span>
                <div>
                  <div className="text-base font-bold text-gray-900 leading-tight">{s.value}</div>
                  <div className="text-xs text-gray-400">{s.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pending banner */}
        {pendingCount > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <i className="ri-time-line text-amber-500 text-lg shrink-0" />
            <div className="flex-1">
              <span className="text-sm font-semibold text-amber-800">{pendingCount} order{pendingCount !== 1 ? 's' : ''} awaiting payment confirmation</span>
              <p className="text-xs text-amber-600 mt-0.5">Crypto payments typically confirm within 1–30 minutes. Your subscription activates automatically.</p>
            </div>
            <div className="shrink-0 text-right">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                <span className="text-xs text-amber-600 font-mono whitespace-nowrap">Auto-refresh in {countdown}s</span>
              </div>
              {lastPolled && <p className="text-[11px] text-amber-500 mt-0.5">Last checked: {lastPolled.toLocaleTimeString()}</p>}
              <button
                onClick={() => { refetch(); setLastPolled(new Date()); setCountdown(30); }}
                className="mt-1 text-[11px] text-amber-600 underline cursor-pointer whitespace-nowrap hover:text-amber-800"
              >
                Refresh now
              </button>
            </div>
          </motion.div>
        )}

        {/* Filters */}
        {orders.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                className="w-full pl-9 pr-4 py-2 text-sm border border-surface-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white"
                placeholder="Search by order ID or product..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all',             label: 'All' },
                { key: 'paid',            label: 'Completed' },
                { key: 'awaiting_payment',label: 'Pending' },
                { key: 'failed',          label: 'Failed' },
              ].map(s => (
                <button
                  key={s.key}
                  onClick={() => setStatusFilter(s.key)}
                  className={`px-3 py-2 text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors ${statusFilter === s.key ? 'bg-sidebar text-white' : 'bg-white border border-surface-3 text-gray-500 hover:border-gray-300'}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-5 animate-pulse">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-xl" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-100 rounded w-32" />
                    <div className="h-3 bg-gray-100 rounded w-20" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-5 bg-gray-100 rounded w-16" />
                    <div className="h-4 bg-gray-100 rounded w-20" />
                  </div>
                </div>
                <div className="h-3 bg-gray-100 rounded w-full" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <i className="ri-error-warning-line text-red-400 text-3xl block mb-3" />
            <p className="text-sm text-gray-500 font-medium">Failed to load orders</p>
            <p className="text-xs text-gray-400 mt-1">{error}</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-xl p-16 text-center">
            <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-shopping-cart-line text-brand-400 text-2xl" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">No orders yet</h3>
            <p className="text-sm text-gray-400 mb-6 max-w-xs mx-auto">
              You haven&apos;t placed any orders yet. Browse the marketplace to find the perfect API for your needs.
            </p>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-store-2-line" />
              Browse Marketplace
            </Link>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <i className="ri-inbox-line text-gray-300 text-3xl block mb-2" />
            <p className="text-sm text-gray-400">No orders match your search</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filtered.map((order, i) => (
              <motion.div key={order.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <OrderCard order={order} onClick={() => setSelected(order)} />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Order detail modal */}
      {selected && (
        <OrderDetailModal
          order={selected}
          onClose={() => setSelected(null)}
          onDownloadInvoice={() => { setInvoiceOrder(selected); setSelected(null); }}
        />
      )}

      {/* Invoice PDF modal */}
      {invoiceOrder && (
        <InvoicePDF
          order={invoiceOrder}
          userName={userName}
          userEmail={userEmail}
          onClose={() => setInvoiceOrder(null)}
        />
      )}
    </AppLayout>
  );
}
